import { useAuth } from '@/context/AuthContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { api } from '@/lib/trpc/react';
import { getPrice } from '@/utils/billingHelpers';
import * as Sentry from '@sentry/nextjs';
import type { BillingInterval, SubscriptionPlan } from '@unriddle/db/types';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { showRewardBannerAtom } from './Banners/RewardBanner';

export const Success = () => {
  const router = useRouter();
  const setShowRewardBanner = useSetAtom(showRewardBannerAtom);
  const { track } = useAnalytics();
  const { user } = useAuth();
  const utils = api.useUtils();

  useEffect(() => {
    const { checkout_complete, plan, billing_interval, session_id } =
      router.query;
    let timeout: NodeJS.Timeout;
    if (checkout_complete === 'true' && plan && billing_interval) {
      track('checkout_completed', {
        plan,
        billing_interval,
        checkout_complete,
      });
      setShowRewardBanner(false);

      if (user?.email) {
        timeout = setTimeout(() => {
          utils.user.byEmail.invalidate(user.email);
        }, 5000);
      }

      const value = plan === 'PREMIUM' ? 29 : plan === 'PRO' ? 19 : 0;

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-16774945281/gN0YCLSBveoZEIG09b4-',
          value,
          currency: 'USD',
          transaction_id: session_id as string,
        });
      }
      try {
        const price = getPrice({
          plan: plan as SubscriptionPlan,
          billingInterval: billing_interval as BillingInterval,
        });

        try {
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (window as any).fbq('track', 'Purchase', {
            value: price,
            currency: 'USD',
            contents: [
              {
                price: price,
                plan: plan,
                billing_interval: billing_interval,
              },
            ],
          });

          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (window as any).rdt('track', 'Purchase', {
            value: price,
            currency: 'USD',
            custom_data: {
              plan: plan,
              billing_interval: billing_interval,
            },
          });
        } catch (e) {
          Sentry.captureException(`Error pixel tracking purchase: ${e}`);
        }

        const {
          checkout_complete,
          plan: p,
          billing_interval: b,
          ...restQuery
        } = router.query;
        router.replace(
          {
            pathname: router.pathname,
            query: restQuery,
          },
          undefined,
          { shallow: true },
        );
      } catch (e) {
        Sentry.captureException(`Error tracking purchase: ${e}`);
      }
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [router.query]);

  return null;
};
